"use client"

/**
 * EPUB Preview panel for use inside flexlayout-react tabs.
 *
 * Renders the two-column EPUB reader content + pagination.
 * Tab header is managed by flexlayout.
 */

import { useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { TabNode } from "flexlayout-react"

const epubContent: Record<number, { left: string; right: string }> = {
  1: {
    left: `The paradigm of scholarly communication has undergone a fundamental shift as the transition from static physical archives to dynamic digital repositories alters the preservation of intellectual capital. This evolution from elementary storage mechanisms into complex ecosystem of versioned manuscripts and collaborative environments represents a significant transformation in the interaction between the author and the written word. In the context of modern document management, the precise balance must be maintained between mathematical rigor and aesthetic charm, often facilitated by the integration of distributed version control systems like Git. Such systems allow researchers to maintain a high-integrity production branch while simultaneously conducting radical experiments within isolated personal sandboxes.

Within these technical environments, the document preamble serves as a critical infrastructure, defining the class and global package requirements necessary for accurate rendering. Every commit to the primary repository undergoes automated verification against an authoritative LaTeX engine to ensure the compilation process components line build process.`,

    right: `As digital infrastructure matures, the reliance on monolithic document structures is being replaced by modular, interlinked components that allow for granular versioning. This modularity ensures that individual sections of a manuscript can be developed, critiqued, and merged independently without interrupting the global compilation of the LaTeX project. The resulting workflow minimizes merge conflicts and maximizes the efficiency of large-scale research groups who must maintain a unified style guide across hundreds of unique document pages.

The integration of automated linting tools further enhances the quality of the technical manuscript by enforcing syntactic consistency before a file reaches the production branch. These tools act as a first line of defense, identifying common errors in LaTeX mathematical notation or bibliography formatting that would otherwise lead to a compilation failure.`,
  },
}

interface EPUBPreviewPanelProps {
  node: TabNode
}

export function EPUBPreviewPanel({ node }: EPUBPreviewPanelProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = 2

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1))
  const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1))

  const content = epubContent[currentPage] ?? epubContent[1]

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* EPUB Content - Two Column Layout */}
      <div className="flex-1 overflow-auto bg-gray-50 dark:bg-slate-900">
        <div className="grid grid-cols-2 gap-6 p-6 max-w-5xl mx-auto">
          {/* Left Column */}
          <div className="bg-white dark:bg-gray-800 rounded p-6 shadow-sm">
            <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300 text-justify">
              {content.left}
            </p>
          </div>

          {/* Right Column */}
          <div className="bg-white dark:bg-gray-800 rounded p-6 shadow-sm">
            <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300 text-justify">
              {content.right}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="h-10 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between px-4 bg-gray-50 dark:bg-slate-800 shrink-0">
        <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">
          {currentPage}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="h-7 w-7 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-gray-600 dark:text-gray-400 w-8 text-center">
            {currentPage}/{totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="h-7 w-7 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">
          {Math.min(currentPage + 1, totalPages)}
        </span>
      </div>
    </div>
  )
}
